import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatWIB, todayWIB } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Plus, FunnelSimple, X, ArrowUp, ArrowDown, CircleNotch, Trash, CaretLeft, CaretRight, CheckSquare, Square, Warning } from "@phosphor-icons/react";

export default function JobListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [panelSources, setPanelSources] = useState([]);
  const [sites, setSites] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const perPage = 20;

  // Create form
  const [siteId, setSiteId] = useState("");
  const [panelSourceId, setPanelSourceId] = useState("");
  const [recordType, setRecordType] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);

  // Filters
  const [filterSite, setFilterSite] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterCreatedBy, setFilterCreatedBy] = useState("all");
  const [filterSearch, setFilterSearch] = useState("");

  // Sort
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  // Selection & delete
  const [selected, setSelected] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showFilterDelete, setShowFilterDelete] = useState(false);
  const [filterDeleteStatus, setFilterDeleteStatus] = useState("");
  const [filterDeleteDate, setFilterDeleteDate] = useState("");

  useEffect(() => { loadJobs(); loadSites(); }, [page]);

  // Poll for running jobs
  const pollRef = useRef(null);
  useEffect(() => {
    const hasRunning = jobs.some(j => j.status === "running" || j.status === "waiting_captcha");
    if (hasRunning && !pollRef.current) {
      pollRef.current = setInterval(() => loadJobs(true), 5000);
    } else if (!hasRunning && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobs]);

  const loadJobs = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const skip = (page - 1) * perPage;
      const { data } = await API.get(`/jobs?limit=${perPage}&skip=${skip}`);
      setJobs(data.jobs);
      setTotal(data.total);
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  };

  const loadSites = async () => {
    try {
      const [p, s] = await Promise.all([API.get("/panel-sources"), API.get("/sites")]);
      setPanelSources(p.data);
      setSites(s.data);
    } catch (e) { console.error(e); }
  };

  const handleCreate = async () => {
    if (!dateFrom || !dateTo) { alert("Pilih tanggal mulai dan akhir"); return; }
    setCreating(true);
    try {
      const { data } = await API.post("/jobs", {
        job_type: "manual", site_id: siteId, panel_source_id: panelSourceId,
        record_type: recordType, date_from: dateFrom, date_to: dateTo, notes,
      });
      setShowCreate(false); loadJobs(); navigate(`/jobs/${data.id}`);
    } catch (e) { alert("Gagal membuat job"); }
    finally { setCreating(false); }
  };

  // Filter options
  const filterOptions = useMemo(() => {
    const createdBySet = new Set();
    const siteSet = new Set();
    jobs.forEach(j => {
      if (j.triggered_by?.name) createdBySet.add(j.triggered_by.name);
      if (j.site_id) {
        const site = sites.find(s => s.id === j.site_id);
        if (site) siteSet.add(site.name);
      }
    });
    return { createdBy: [...createdBySet].sort(), siteNames: [...siteSet].sort() };
  }, [jobs, sites]);

  // Apply filters + sort (client-side on current page)
  const filteredJobs = useMemo(() => {
    let result = [...jobs];
    if (filterStatus !== "all") result = result.filter(j => j.status === filterStatus);
    if (filterType !== "all") result = result.filter(j => (j.record_type || "all") === filterType);
    if (filterCreatedBy !== "all") result = result.filter(j => j.triggered_by?.name === filterCreatedBy);
    if (filterSite !== "all") {
      result = result.filter(j => {
        const site = sites.find(s => s.id === j.site_id);
        return site?.name === filterSite;
      });
    }
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      result = result.filter(j =>
        (j.id || "").toLowerCase().includes(q) ||
        (j.notes || "").toLowerCase().includes(q) ||
        (j.date_from || "").includes(q) ||
        (j.date_to || "").includes(q)
      );
    }

    result.sort((a, b) => {
      let va = a[sortField] ?? "";
      let vb = b[sortField] ?? "";
      if (["total_records","matched_count","pg_count","review_count","unmatched_count"].includes(sortField)) {
        va = Number(va) || 0; vb = Number(vb) || 0;
      }
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [jobs, filterStatus, filterType, filterCreatedBy, filterSite, filterSearch, sortField, sortDir, sites]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const activeFilters = [filterSite, filterStatus, filterType, filterCreatedBy].filter(f => f !== "all").length + (filterSearch ? 1 : 0);

  const resetFilters = () => {
    setFilterSite("all"); setFilterStatus("all"); setFilterType("all");
    setFilterCreatedBy("all"); setFilterSearch("");
  };

  const today = todayWIB();

  const getSiteName = (siteId) => {
    if (!siteId) return "-";
    const site = sites.find(s => s.id === siteId);
    return site ? site.name : "-";
  };

  // Selection
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredJobs.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredJobs.map(j => j.id)));
    }
  };

  // Delete handlers
  const handleSingleDelete = async (jobId, e) => {
    e.stopPropagation();
    if (!window.confirm("Pindahkan job ini ke Trash?")) return;
    try {
      await API.delete(`/jobs/${jobId}`);
      loadJobs();
    } catch (e) { alert(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const handleBulkDelete = async () => {
    if (deleteConfirmText !== "HAPUS") return;
    setDeleting(true);
    try {
      await API.post("/jobs/bulk-delete", { job_ids: [...selected] });
      setSelected(new Set());
      setShowDeleteConfirm(false);
      setDeleteConfirmText("");
      loadJobs();
    } catch (e) { alert(e.response?.data?.detail || "Gagal menghapus"); }
    finally { setDeleting(false); }
  };

  const handleFilterDelete = async () => {
    if (deleteConfirmText !== "HAPUS") return;
    setDeleting(true);
    try {
      const payload = {};
      if (filterDeleteStatus) payload.status = filterDeleteStatus;
      if (filterDeleteDate) payload.date_before = filterDeleteDate;
      const { data } = await API.post("/jobs/bulk-delete-by-filter", payload);
      alert(`${data.count} job dipindahkan ke Trash`);
      setShowFilterDelete(false);
      setDeleteConfirmText("");
      loadJobs();
    } catch (e) { alert(e.response?.data?.detail || "Gagal menghapus"); }
    finally { setDeleting(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div data-testid="job-list-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">Jobs</h1>
          <p className="text-sm text-zinc-500 mt-1">{total} total jobs</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && selected.size > 0 && (
            <Button data-testid="bulk-delete-btn" variant="outline" onClick={() => { setShowDeleteConfirm(true); setDeleteConfirmText(""); }}
              className="rounded-sm gap-2 text-red-600 border-red-300 hover:bg-red-50">
              <Trash size={14} /> Hapus {selected.size} Job
            </Button>
          )}
          {isAdmin && (
            <Button data-testid="filter-delete-btn" variant="outline" onClick={() => { setShowFilterDelete(true); setDeleteConfirmText(""); setFilterDeleteStatus(""); setFilterDeleteDate(""); }}
              className="rounded-sm gap-2 text-zinc-600 border-zinc-300 hover:bg-zinc-50 text-xs">
              <Trash size={14} /> Hapus by Filter
            </Button>
          )}
          <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (open && !dateFrom) { setDateFrom(today); setDateTo(today); } }}>
            <DialogTrigger asChild>
              <Button data-testid="create-job-btn" className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm gap-2"><Plus size={16} /> Job Baru</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Buat Job Rekonsiliasi</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Site</Label>
                  <select data-testid="select-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:ring-1 focus:ring-zinc-900 outline-none">
                    <option value="">Default (mock data)</option>
                    {sites.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Tanggal Mulai</Label>
                    <Input data-testid="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm border-zinc-300 font-mono" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Tanggal Akhir</Label>
                    <Input data-testid="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm border-zinc-300 font-mono" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Tipe Transaksi</Label>
                  <select data-testid="record-type" value={recordType} onChange={(e) => setRecordType(e.target.value)} className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:ring-1 focus:ring-zinc-900 outline-none">
                    <option value="all">Semua (Deposit + Withdraw)</option>
                    <option value="deposit">Deposit saja</option>
                    <option value="withdraw">Withdraw saja</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Panel Source</Label>
                  <select data-testid="select-panel-source" value={panelSourceId} onChange={(e) => setPanelSourceId(e.target.value)} className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:ring-1 focus:ring-zinc-900 outline-none">
                    <option value="">Default (mock)</option>
                    {panelSources.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Catatan</Label>
                  <Input data-testid="job-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opsional" className="rounded-sm border-zinc-300" />
                </div>
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-xs text-zinc-500">
                  <p className="font-semibold text-zinc-700 mb-1">Alur kerja:</p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li>Login ke panel {siteId ? getSiteName(siteId) : "PGRAJA"}</li>
                    <li>Scrape transaksi {recordType === "all" ? "deposit & withdraw" : recordType} tanggal {dateFrom || "..."} s/d {dateTo || "..."}</li>
                    <li>Klasifikasi manual vs payment gateway</li>
                    <li>Cocokkan transaksi manual dengan Google Sheet</li>
                  </ol>
                </div>
                <Button data-testid="submit-job-btn" onClick={handleCreate} disabled={creating} className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm">
                  {creating ? "Membuat..." : "Buat Job"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-zinc-200 rounded-sm">
        <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
            <FunnelSimple size={16} /> Filter
            {activeFilters > 0 && <span className="bg-zinc-900 text-white text-[10px] px-1.5 py-0.5 rounded-full">{activeFilters}</span>}
          </button>
          <div className="flex items-center gap-3 text-xs text-zinc-500 font-mono">
            <span>Hal. {page}/{totalPages} ({filteredJobs.length} ditampilkan)</span>
            {activeFilters > 0 && <button onClick={resetFilters} className="text-red-500 hover:text-red-700 flex items-center gap-1"><X size={12} /> Reset</button>}
          </div>
        </div>
        {showFilters && (
          <div className="p-3 grid grid-cols-2 md:grid-cols-5 gap-2">
            <FS label="Site" value={filterSite} onChange={setFilterSite} testId="filter-site" options={[{v:"all",l:"Semua"}, ...filterOptions.siteNames.map(n => ({v:n,l:n}))]} />
            <FS label="Status" value={filterStatus} onChange={setFilterStatus} testId="filter-job-status" options={[{v:"all",l:"Semua"},{v:"pending",l:"Pending"},{v:"running",l:"Running"},{v:"completed",l:"Completed"},{v:"completed_with_errors",l:"Has Issues"},{v:"failed",l:"Failed"}]} />
            <FS label="Tipe" value={filterType} onChange={setFilterType} testId="filter-job-type" options={[{v:"all",l:"Semua"},{v:"deposit",l:"Deposit"},{v:"withdraw",l:"Withdraw"}]} />
            <FS label="Dibuat Oleh" value={filterCreatedBy} onChange={setFilterCreatedBy} testId="filter-created-by" options={[{v:"all",l:"Semua"}, ...filterOptions.createdBy.map(n => ({v:n,l:n}))]} />
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Cari</Label>
              <Input data-testid="filter-job-search" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="ID, tanggal, catatan..." className="rounded-sm border-zinc-300 h-8 text-xs" />
            </div>
          </div>
        )}
      </div>

      {/* Jobs Table */}
      <div className="border border-zinc-200 rounded-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                {isAdmin && (
                  <th className="px-2 py-2 w-8">
                    <button data-testid="select-all-checkbox" onClick={toggleSelectAll} className="text-zinc-400 hover:text-zinc-900">
                      {selected.size > 0 && selected.size === filteredJobs.length ? <CheckSquare size={16} weight="fill" className="text-zinc-900" /> : <Square size={16} />}
                    </button>
                  </th>
                )}
                <STh field="id" label="ID" onSort={handleSort} current={sortField} dir={sortDir} />
                <STh field="site_id" label="Site" onSort={handleSort} current={sortField} dir={sortDir} />
                <STh field="record_type" label="Tipe" onSort={handleSort} current={sortField} dir={sortDir} />
                <STh field="date_from" label="Tanggal Data" onSort={handleSort} current={sortField} dir={sortDir} />
                <STh field="status" label="Status" onSort={handleSort} current={sortField} dir={sortDir} />
                <STh field="total_records" label="Total" onSort={handleSort} current={sortField} dir={sortDir} align="right" />
                <STh field="matched_count" label="Matched" onSort={handleSort} current={sortField} dir={sortDir} align="right" />
                <STh field="pg_count" label="PG" onSort={handleSort} current={sortField} dir={sortDir} align="right" />
                <STh field="review_count" label="Review" onSort={handleSort} current={sortField} dir={sortDir} align="right" />
                <STh field="duplicate_count" label="Duplikat" onSort={handleSort} current={sortField} dir={sortDir} align="right" />
                <STh field="created_at" label="Dibuat" onSort={handleSort} current={sortField} dir={sortDir} />
                {isAdmin && <th className="px-2 py-2 w-8" />}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={13} className="px-4 py-8 text-center text-sm text-zinc-400">Loading...</td></tr>
              ) : filteredJobs.length === 0 ? (
                <tr><td colSpan={13} className="px-4 py-8 text-center text-sm text-zinc-400">Tidak ada job yang sesuai filter</td></tr>
              ) : filteredJobs.map((job) => (
                <tr key={job.id} className={`border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors ${selected.has(job.id) ? "bg-blue-50 hover:bg-blue-100" : ""}`}
                  onClick={() => navigate(`/jobs/${job.id}`)}>
                  {isAdmin && (
                    <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                      <button data-testid={`select-job-${job.id?.slice(0,8)}`} onClick={(e) => toggleSelect(job.id, e)} className="text-zinc-400 hover:text-zinc-900">
                        {selected.has(job.id) ? <CheckSquare size={16} weight="fill" className="text-zinc-900" /> : <Square size={16} />}
                      </button>
                    </td>
                  )}
                  <td className="px-3 py-2 text-xs font-mono text-zinc-900">{job.id?.slice(0, 8)}</td>
                  <td className="px-3 py-2">
                    {job.site_id ? (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-zinc-900 text-white">
                        {getSiteName(job.site_id)?.slice(0, 8) || "-"}
                      </span>
                    ) : <span className="text-[10px] text-zinc-400">-</span>}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono uppercase">{job.record_type || "all"}</td>
                  <td className="px-3 py-2 text-xs font-mono text-zinc-600">{job.date_from} ~ {job.date_to}</td>
                  <td className="px-3 py-2">
                    {job.status === "running" ? (
                      <span className="inline-flex items-center gap-1.5">
                        <CircleNotch size={12} className="text-blue-500 animate-spin" />
                        <StatusBadge status={job.status} />
                      </span>
                    ) : (
                      <StatusBadge status={job.status} />
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-right">{job.total_records}</td>
                  <td className="px-3 py-2 text-xs font-mono text-right text-emerald-600">{job.matched_count || 0}</td>
                  <td className="px-3 py-2 text-xs font-mono text-right text-violet-600">{job.pg_count || 0}</td>
                  <td className="px-3 py-2 text-xs font-mono text-right text-amber-600">{job.review_count || 0}</td>
                  <td className="px-3 py-2 text-xs font-mono text-right">{job.duplicate_count ? <span className="text-amber-500 font-semibold">{job.duplicate_count}</span> : <span className="text-zinc-300">0</span>}</td>
                  <td className="px-3 py-2 text-[10px] font-mono text-zinc-500 whitespace-nowrap">{formatWIB(job.created_at)}</td>
                  {isAdmin && (
                    <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                      <button data-testid={`delete-job-${job.id?.slice(0,8)}`} onClick={(e) => handleSingleDelete(job.id, e)}
                        className="text-zinc-300 hover:text-red-500 transition-colors" title="Pindahkan ke Trash">
                        <Trash size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
          <p className="text-xs text-zinc-500 font-mono">
            {total > 0 ? `${(page-1)*perPage + 1}-${Math.min(page*perPage, total)} dari ${total}` : "0 jobs"}
          </p>
          <div className="flex items-center gap-1">
            <Button data-testid="prev-page" variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="rounded-sm h-7 w-7 p-0"><CaretLeft size={14} /></Button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum;
              if (totalPages <= 7) { pageNum = i + 1; }
              else if (page <= 4) { pageNum = i + 1; }
              else if (page >= totalPages - 3) { pageNum = totalPages - 6 + i; }
              else { pageNum = page - 3 + i; }
              return (
                <Button key={pageNum} variant={page === pageNum ? "default" : "outline"} size="sm"
                  onClick={() => setPage(pageNum)}
                  className={`rounded-sm h-7 w-7 p-0 text-xs ${page === pageNum ? "bg-zinc-900 text-white" : ""}`}>
                  {pageNum}
                </Button>
              );
            })}
            <Button data-testid="next-page" variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="rounded-sm h-7 w-7 p-0"><CaretRight size={14} /></Button>
          </div>
        </div>
      </div>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-red-700">Konfirmasi Hapus Job</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex gap-2">
              <Warning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800">
                <p className="font-semibold">Anda akan memindahkan {selected.size} job ke Trash.</p>
                <p className="mt-1">Job di Trash akan otomatis dihapus permanen setelah 7 hari. Anda bisa mengembalikannya sebelum itu.</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-zinc-600">Ketik <span className="font-bold text-red-600">HAPUS</span> untuk konfirmasi:</Label>
              <Input data-testid="delete-confirm-input" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Ketik HAPUS" className="rounded-sm border-zinc-300 font-mono" autoFocus />
            </div>
            <div className="flex gap-2">
              <Button data-testid="confirm-bulk-delete" onClick={handleBulkDelete} disabled={deleteConfirmText !== "HAPUS" || deleting}
                className="flex-1 bg-red-600 text-white hover:bg-red-700 rounded-sm">
                {deleting ? "Menghapus..." : "Hapus ke Trash"}
              </Button>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="rounded-sm">Batal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filter Delete Dialog */}
      <Dialog open={showFilterDelete} onOpenChange={setShowFilterDelete}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Hapus Job Berdasarkan Filter</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Status Job</Label>
                <select data-testid="filter-delete-status" value={filterDeleteStatus} onChange={e => setFilterDeleteStatus(e.target.value)}
                  className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm outline-none">
                  <option value="">Semua Status</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="completed_with_errors">Has Issues</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Dibuat Sebelum Tanggal</Label>
                <Input data-testid="filter-delete-date" type="date" value={filterDeleteDate} onChange={e => setFilterDeleteDate(e.target.value)}
                  className="rounded-sm border-zinc-300 font-mono" />
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800">
              Job yang cocok akan dipindahkan ke Trash (bukan hapus permanen). Anda bisa mengembalikan dari Trash dalam 7 hari.
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-zinc-600">Ketik <span className="font-bold text-red-600">HAPUS</span> untuk konfirmasi:</Label>
              <Input data-testid="filter-delete-confirm-input" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Ketik HAPUS" className="rounded-sm border-zinc-300 font-mono" />
            </div>
            <div className="flex gap-2">
              <Button data-testid="confirm-filter-delete" onClick={handleFilterDelete} disabled={deleteConfirmText !== "HAPUS" || deleting || (!filterDeleteStatus && !filterDeleteDate)}
                className="flex-1 bg-red-600 text-white hover:bg-red-700 rounded-sm">
                {deleting ? "Menghapus..." : "Hapus ke Trash"}
              </Button>
              <Button variant="outline" onClick={() => setShowFilterDelete(false)} className="rounded-sm">Batal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FS({ label, value, onChange, options, testId }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</Label>
      <select data-testid={testId} value={value} onChange={e => onChange(e.target.value)}
        className="w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-xs focus:ring-1 focus:ring-zinc-900 outline-none bg-white">
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function STh({ field, label, onSort, current, dir, align = "left" }) {
  const isActive = current === field;
  return (
    <th
      className={`${align === "right" ? "text-right" : "text-left"} px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] cursor-pointer select-none transition-colors ${isActive ? "text-zinc-900 bg-zinc-100" : "text-zinc-500 hover:text-zinc-700"}`}
      onClick={() => onSort(field)}
    >
      {label}
      {isActive && (dir === "asc" ? <ArrowUp size={9} className="inline ml-0.5" /> : <ArrowDown size={9} className="inline ml-0.5" />)}
    </th>
  );
}
